import Vue from 'vue'
import * as Util from './Util'
import FilterPanelUtil from './FilterPanelUtil'
import { MONTHS, WEEKDAYS, RATE_TYPES } from './AttendanceConstants'

/**
 * Cache for filter API data to avoid redundant calls within a page session.
 * Cleared when navigating away from Students 3D.
 */
const filterDataCache = {
    gender: null,
    ethnicity: null,
    disability: null,
    mealStatus: null,
    tag: null,
}

/**
 * Clear the filter data cache (call when leaving Students 3D page).
 */
export function clearFilterCache() {
    filterDataCache.gender = null
    filterDataCache.ethnicity = null
    filterDataCache.disability = null
    filterDataCache.mealStatus = null
    filterDataCache.tag = null
    filterDataCache.schoolYears = null
    // Also clear any pending requests
    Object.keys(pendingRequests).forEach(key => delete pendingRequests[key])
}

/**
 * Track in-flight API requests to prevent race conditions
 */
const pendingRequests = {}

/**
 * Generic fetch and cache function for filter API data.
 * Returns null on error to signal caller to abort (rather than providing incomplete data).
 *
 * @param {Object} viewComponent - Vue component instance with $modelGet
 * @param {string} cacheKey - Key in filterDataCache ('gender', 'ethnicity', 'disability')
 * @param {string} modelName - Model name for $modelGet
 * @param {Object} params - Additional params for API call (time param added automatically)
 * @param {Function} extractData - Function to extract array from response
 * @returns {Promise<Array|null>} Array of filter objects, or null if API fails
 */
async function fetchAndCacheFilterData(viewComponent, cacheKey, modelName, params = {}, extractData = (r) => r) {
    // Return cached data if available
    if (filterDataCache[cacheKey]) {
        return filterDataCache[cacheKey]
    }

    // If request already in flight, wait for it to prevent duplicate API calls
    if (pendingRequests[cacheKey]) {
        return pendingRequests[cacheKey]
    }

    // Start new request and track it
    const promise = (async () => {
        try {
            // Note: time parameter intentionally busts cache to ensure fresh data.
            // Gender/ethnicity/disability options can be modified by admins and must reflect
            // current state. Without this, users could apply filters using stale/deleted values.
            const response = await viewComponent.$modelGet(modelName, { ...params, time: Date.now() })
            const data = extractData(response) || []
            filterDataCache[cacheKey] = data
            return data
        } catch (error) {
            console.error(`Error fetching ${cacheKey} data:`, error)
            // Return null to signal failure - caller should abort rather than use incomplete data
            return null
        } finally {
            delete pendingRequests[cacheKey]
        }
    })()

    pendingRequests[cacheKey] = promise
    return promise
}

/**
 * Fetch and cache gender options from API.
 * @param {Object} viewComponent - Vue component instance with $modelGet
 * @returns {Promise<Array|null>} Array of gender objects with display_name_group, or null if API fails
 */
async function fetchAndCacheGenders(viewComponent) {
    return fetchAndCacheFilterData(viewComponent, 'gender', 'gender')
}

/**
 * Fetch and cache ethnicity options from API.
 * @param {Object} viewComponent - Vue component instance with $modelGet
 * @returns {Promise<Array|null>} Array of ethnicity objects with display_name_group, or null if API fails
 */
async function fetchAndCacheEthnicities(viewComponent) {
    return fetchAndCacheFilterData(viewComponent, 'ethnicity', 'ethnicity')
}

/**
 * Fetch and cache disability options from API.
 * @param {Object} viewComponent - Vue component instance with $modelGet
 * @param {number} schoolYearId - School year ID for filtering disabilities
 * @returns {Promise<Array|null>} Array of disability objects with display_name_group, or null if API fails
 */
async function fetchAndCacheDisabilities(viewComponent, schoolYearId) {
    return fetchAndCacheFilterData(
        viewComponent,
        'disability',
        'studentDisabilityType',
        { school_year_id: schoolYearId },
        (response) => {
            // $modelGet can return array directly or nested in different structures
            return Array.isArray(response)
                ? response
                : (response?.data?.student_disability_type ?? response?.student_disability_type ?? [])
        }
    )
}

async function fetchAndCacheMealStatuses(viewComponent) {
    return fetchAndCacheFilterData(
        viewComponent,
        'mealStatus',
        'mealStatusType',
        {},
        (response) => {
            return Array.isArray(response)
                ? response
                : (response?.data?.meal_status ?? response?.meal_status ?? [])
        }
    )
}

export const MEAL_STATUS_SECURITY = ['SEARCH_MEAL_STATUS', 'DRILLDOWN_MEAL_STATUS']

export function canSearchByMealStatus(viewComponent) {
    return !!viewComponent?.$hasSecurity?.(MEAL_STATUS_SECURITY)
}

/**
 * Fetch and cache active tag options from API.
 * @param {Object} viewComponent - Vue component instance with $modelGet
 * @returns {Promise<Array|null>} Array of tag objects, or null if API fails
 */
async function fetchAndCacheTags(viewComponent) {
    return fetchAndCacheFilterData(
        viewComponent,
        'tag',
        'tag',
        { active: 1 },
        (response) => {
            return Array.isArray(response)
                ? response
                : (response?.data?.tags ?? response?.tags ?? [])
        }
    )
}

/**
 * Apply search params from nlf-agent to Students 3D params.search and sync the demographic search UI.
 * The agent sends school_year_id + school_year_name as flat fields, and full objects for schools/grades.
 *
 * Anything the agent omits falls back to the search bar's current value. Only agent-specified
 * fields get confirmed in the returned message — backfilled values stay silent.
 *
 * @param {Object} agentSearch - search params from nlf-agent result
 * @param {Object} context - { demographicSearchRef }
 * @returns {Object} { updates, message } - search params to merge and notification message
 */
export function applySearchParams(agentSearch, context) {
    if (!agentSearch) return null

    const { demographicSearchRef } = context
    const updates = {}
    const appliedItems = []
    const barDefaults = demographicSearchRef?.getCurrentSearchParams?.() || {}

    try {
        // Set flag to prevent updateActiveDate from being called by watchers
        if (demographicSearchRef) {
            demographicSearchRef.skipActiveDateUpdate = true
        }

        // Only touch the school year when the agent explicitly names a different one.
        if (agentSearch.school_year_id && agentSearch.school_year_name && demographicSearchRef) {
            demographicSearchRef.schoolYear = {
                id: agentSearch.school_year_id,
                name: agentSearch.school_year_name,
            }
            appliedItems.push(`School Year: **${agentSearch.school_year_name}**`)
        }
        const schoolYearId = agentSearch.school_year_id || barDefaults.school_year_id
        if (schoolYearId) {
            updates.school_year_id = schoolYearId
        }

        // Update schools (full objects array)
        if (agentSearch.schools && Array.isArray(agentSearch.schools) && agentSearch.schools.length) {
            if (demographicSearchRef) {
                demographicSearchRef.schools = agentSearch.schools
            }
            updates.school_id = agentSearch.schools.map(s => s.id)
            const schoolNames = agentSearch.schools.map(s => s.name).join(', ')
            appliedItems.push(`Schools: **${schoolNames}**`)
        } else if (barDefaults.school_id) {
            updates.school_id = barDefaults.school_id
        }

        // Update grades (full objects array)
        if (agentSearch.grades && Array.isArray(agentSearch.grades) && agentSearch.grades.length) {
            if (demographicSearchRef) {
                demographicSearchRef.grades = agentSearch.grades
            }
            updates.grade_id = agentSearch.grades.map(g => g.id)
            const gradeNames = agentSearch.grades.map(g => g.name).join(', ')
            appliedItems.push(`Grades: **${gradeNames}**`)
        } else if (barDefaults.grade_id) {
            updates.grade_id = barDefaults.grade_id
        }

        // Fall back to the search bar's active-status value if the agent doesn't specify one.
        const activeFlag = agentSearch.student_active_flag !== undefined
            ? agentSearch.student_active_flag
            : barDefaults.student_active_flag
        if (activeFlag !== undefined) {
            if (demographicSearchRef) {
                demographicSearchRef.student_active_flag = activeFlag
            }
            updates.student_active_flag = activeFlag
            if (agentSearch.student_active_flag !== undefined) {
                const statusText = activeFlag === 1 ? 'Active' : activeFlag === 0 ? 'Inactive' : 'Both'
                appliedItems.push(`Status: **${statusText}**`)
            }
        }

        const activeDate = agentSearch.student_active_date || barDefaults.student_active_date
        if (activeDate) {
            if (demographicSearchRef) {
                demographicSearchRef.student_active_date = activeDate
            }
            updates.student_active_date = activeDate
            if (agentSearch.student_active_date) {
                appliedItems.push(`Active as of: **${activeDate}**`)
            }
        }

        const message = appliedItems.length > 0
            ? `Search updated!\n${appliedItems.join('\n')}`
            : null

        // Reset flag after Vue processes all reactive updates
        if (demographicSearchRef) {
            demographicSearchRef.$nextTick(() => {
                demographicSearchRef.skipActiveDateUpdate = false
            })
        }

        return { updates, message }
    } catch (error) {
        // On error, reset flag immediately
        if (demographicSearchRef) {
            demographicSearchRef.skipActiveDateUpdate = false
        }
        throw error
    }
}

/**
 * Replace an existing filter in state, managing both filters and groupers arrays.
 * Removes old filter + its grouper, adds new filter + its grouper.
 *
 * @param {Array} filters - Current filters array
 * @param {Array} groupers - Current groupers array
 * @param {string} filterType - Type of filter to replace
 * @param {Object} newFilter - New filter to add
 * @returns {Object} { filters, groupers } - Updated arrays
 */
function replaceFilterInState(filters, groupers, filterType, newFilter) {
    // Remove all filters of this type and add the new one
    const updatedFilters = [...filters.filter(f => f.type !== filterType), newFilter]

    // Remove groupers that belong to old filters of this type
    // (grouper.id matches the filter.id for the filter that created it)
    const oldFilterIds = filters.filter(f => f.type === filterType).map(f => f.id)
    let updatedGroupers = groupers.filter(g => !oldFilterIds.includes(g.id))

    // Add the new grouper if present
    if (newFilter.grouper) {
        updatedGroupers.push(newFilter.grouper)
    }

    return { filters: updatedFilters, groupers: updatedGroupers }
}

/**
 * Apply a gender filter from nlf-agent to Students 3D state.
 */
export async function applyGenderFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    const genderValues = result.params?.gender || []
    const displayNames = result.params?.display_name || []

    if (!genderValues.length) {
        msg.content = 'No gender values provided'
        return null
    }

    // Fetch full API data to get display_name_group
    const allGenders = await fetchAndCacheGenders(viewComponent)

    // If API fetch failed, abort rather than providing incomplete student data
    if (!allGenders) {
        msg.content = 'Unable to apply gender filter at this time. Please try again in a moment, or contact support if the issue persists.'
        return null
    }

    // Match agent's selected values to full API objects (which have display_name_group)
    const genderObjects = genderValues.map((value, idx) => {
        const apiObject = allGenders.find(g => g.value === value || g.display_name === displayNames[idx])
        return {
            value,
            display_name: displayNames[idx] || value,
            display_name_group: apiObject?.display_name_group,
        }
    })

    // Build chip text from display_name array
    const chipText = displayNames.join(', ')

    // Expand display_name_group to get all gender values for API params
    const allGenderValues = FilterPanelUtil.collectIdsFromSelection(genderObjects, { idKey: 'value', parseInteger: false })

    const uniqueId = Util.UUIDv4()

    const filter = {
        type: 'gender',
        id: uniqueId,
        report: true,
        params: { gender: allGenderValues },
    }

    // Get reference to GenderPanel if available
    const genderPanelRef = viewComponent?.$refs?.GenderPanel?.[0] || null

    // Attach descriptor properties using the shared utility
    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: genderPanelRef || { load: () => {} }, // Use actual GenderPanel ref if available
        editorParams: { genders: genderObjects },
        chipText,
        tooltipData: { Gender: chipText },
        grouper: null, // Gender doesn't create a grouper
    })

    // Load the filter into the GenderPanel to sync its internal state (for button highlighting)
    if (genderPanelRef && typeof genderPanelRef.load === 'function') {
        try {
            genderPanelRef.load(filter)
        } catch (error) {
            console.error('Error loading filter into GenderPanel:', error)
        }
    }

    // Replace in state
    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        'gender',
        filter
    )

    const message = `Gender: **${chipText}**`
    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}

/**
 * Apply an ethnicity filter from nlf-agent to Students 3D state.
 */
export async function applyEthnicityFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    const ethnicityValues = result.params?.ethnicity || []
    const displayNames = result.params?.display_name || []

    if (!ethnicityValues.length) {
        msg.content = 'No ethnicity values provided'
        return null
    }

    // Fetch full API data to get display_name_group
    const allEthnicities = await fetchAndCacheEthnicities(viewComponent)

    // If API fetch failed, abort rather than providing incomplete student data
    if (!allEthnicities) {
        msg.content = 'Unable to apply ethnicity filter at this time. Please try again in a moment, or contact support if the issue persists.'
        return null
    }

    // Match agent's selected values to full API objects (which have display_name_group)
    const ethnicityObjects = ethnicityValues.map((value, idx) => {
        const apiObject = allEthnicities.find(e => e.value === value || e.display_name === displayNames[idx])
        return {
            value,
            display_name: displayNames[idx] || value,
            display_name_group: apiObject?.display_name_group,
        }
    })

    // Build chip text from display_name array
    const chipText = displayNames.join(', ')

    // Expand display_name_group to get all ethnicity values for API params
    const allEthnicityValues = FilterPanelUtil.collectIdsFromSelection(ethnicityObjects, { idKey: 'value', parseInteger: false })

    const uniqueId = Util.UUIDv4()

    const filter = {
        type: 'ethnicity',
        id: uniqueId,
        report: true,
        params: { ethnicity: allEthnicityValues },
    }

    // Get reference to EthnicityPanel if available (it's in MoreFiltersPanel)
    const moreFiltersRef = viewComponent?.$refs?.MoreFiltersPanel?.[0]
    const ethnicityPanelRef = moreFiltersRef?.$refs?.ethnicityPanel

    // Attach descriptor properties using the shared utility
    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: ethnicityPanelRef,
        editorParams: { ethnicities: ethnicityObjects },
        chipText,
        tooltipData: { Ethnicity: chipText },
        grouper: null, // Ethnicity doesn't create a grouper
    })

    // Replace in state
    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        'ethnicity',
        filter
    )

    const message = `Ethnicity: **${chipText}**`
    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}

/**
 * Apply a disability filter from nlf-agent to Students 3D state.
 */
export async function applyDisabilityFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    const disabilityIds = result.params?.student_disability_type_id || []
    const displayNames = result.params?.display_name || []
    const restrict = result.restrict !== undefined ? result.restrict : true
    const requireAll = result.params?.require_all || false

    if (!disabilityIds.length) {
        msg.content = 'No disability IDs provided'
        return null
    }

    // Get school year ID from multiple sources
    let schoolYearId = result.params?.school_year_id
    if (!schoolYearId) {
        schoolYearId = context.params?.search?.school_year_id
    }
    if (!schoolYearId && viewComponent?.$refs?.demographicSearch) {
        const searchParams = viewComponent.$refs.demographicSearch.getCurrentSearchParams?.()
        schoolYearId = searchParams?.school_year_id
    }
    if (!schoolYearId) {
        msg.content = 'School year ID is required for disability filters'
        return null
    }

    // Fetch full API data to get display_name_group
    const allDisabilities = await fetchAndCacheDisabilities(viewComponent, schoolYearId)

    // If API fetch failed, abort rather than providing incomplete student data
    if (!allDisabilities) {
        msg.content = 'Unable to apply disability filter at this time. Please try again in a moment, or contact support if the issue persists.'
        return null
    }

    // Match agent's selected IDs to full API objects (which have display_name_group)
    const disabilityObjects = disabilityIds.map((id, idx) => {
        const apiObject = allDisabilities.find(d => d.id === id || d.display_name === displayNames[idx] || d.description === displayNames[idx])
        return {
            id,
            description: displayNames[idx] || id,
            display_name: displayNames[idx] || id,
            display_name_group: apiObject?.display_name_group,
        }
    })

    // Build chip text from display_name array
    const chipText = displayNames.join(', ')

    // Expand display_name_group to get all disability IDs for API params
    const allDisabilityIds = FilterPanelUtil.collectIdsFromSelection(disabilityObjects, { idKey: 'id', parseInteger: true })

    const uniqueId = Util.UUIDv4()

    const filter = {
        type: 'disability',
        id: uniqueId,
        restrict,
        report: true,
        params: {
            student_disability_type_id: allDisabilityIds,
            school_year_id: schoolYearId,
            ...(requireAll ? { require_all: true } : {}),
        },
    }

    // Get reference to DisabilityPanel if available (it's in MoreFiltersPanel)
    const moreFiltersRef = viewComponent?.$refs?.MoreFiltersPanel?.[0]
    const disabilityPanelRef = moreFiltersRef?.$refs?.disabilityPanel

    // Attach descriptor properties using the shared utility
    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: disabilityPanelRef,
        editorParams: {
            disabilities: disabilityObjects,
            restrict,
            require_all: requireAll,
            school_year: { id: schoolYearId }
        },
        chipText,
        tooltipData: {
            'Disability': chipText,
            'Restrict': restrict,
            'Require All': requireAll,
            'School Year': schoolYearId
        },
        grouper: {
            id: uniqueId,
            name: chipText,
            type: 'propertyArray',
            args: { path: `filters.${uniqueId}`, property: 'value' }
        }
    })

    // Replace in state
    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        'disability',
        filter
    )

    const message = `Disability: **${chipText}**`
    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}

export async function applyMealStatusFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    if (!canSearchByMealStatus(viewComponent)) {
        msg.content = 'You do not have access to search by meal status.'
        return null
    }

    const mealStatusIds = result.params?.meal_status_id || []
    const displayNames = result.params?.display_name || []
    const restrict = result.restrict !== undefined ? result.restrict : true

    if (!mealStatusIds.length) {
        msg.content = 'No meal status IDs provided'
        return null
    }

    let schoolYearId = result.params?.school_year_id
    if (!schoolYearId) {
        schoolYearId = context.params?.search?.school_year_id
    }
    if (!schoolYearId && viewComponent?.$refs?.demographicSearch) {
        const searchParams = viewComponent.$refs.demographicSearch.getCurrentSearchParams?.()
        schoolYearId = searchParams?.school_year_id
    }
    if (!schoolYearId) {
        msg.content = 'School year ID is required for meal status filters'
        return null
    }

    const allMealStatuses = await fetchAndCacheMealStatuses(viewComponent)

    // If API fetch failed, abort rather than providing incomplete student data
    if (!allMealStatuses) {
        msg.content = 'Unable to apply meal status filter at this time. Please try again in a moment, or contact support if the issue persists.'
        return null
    }

    // Drop IDs the API doesn't know
    const mealStatusObjects = []
    mealStatusIds.forEach((id, idx) => {
        const apiObject = allMealStatuses.find(m => sameId(m.id, id))
        if (!apiObject) return
        const label = displayNames[idx] || apiObject.display_name || apiObject.name
        mealStatusObjects.push({
            id: apiObject.id,
            name: label,
            display_name: label,
            display_name_group: apiObject.display_name_group,
        })
    })

    if (!mealStatusObjects.length) {
        msg.content = 'None of those meal statuses are available. Please try again.'
        return null
    }

    const searchBar = viewComponent?.$refs?.demographicSearch
    let schoolYear = null
    if (searchBar?.schoolYear?.id === schoolYearId) {
        schoolYear = { ...searchBar.schoolYear }
    } else {
        const schoolYears = await fetchAndCacheFilterData(
            viewComponent,
            'schoolYears',
            'schoolYear',
            {},
            (response) => response ?? []
        )
        schoolYear = schoolYears?.find(sy => sy.id === schoolYearId) || null
    }

    const chipText = mealStatusObjects.map(m => m.display_name).join(', ')

    const allMealStatusIds = FilterPanelUtil.collectIdsFromSelection(mealStatusObjects, { idKey: 'id', parseInteger: true })

    const uniqueId = Util.UUIDv4()

    const filter = {
        type: 'meal_status',
        id: uniqueId,
        restrict,
        report: true,
        params: {
            meal_status_id: allMealStatusIds,
            school_year_id: schoolYearId,
        },
    }

    const moreFiltersRef = viewComponent?.$refs?.MoreFiltersPanel?.[0]
    const mealStatusPanelRef = moreFiltersRef?.$refs?.mealStatusPanel

    const tooltipData = {
        'Meal Status': chipText,
        'Restrict': restrict,
    }
    if (schoolYear?.name) tooltipData['School Year'] = schoolYear.name

    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: mealStatusPanelRef,
        editorParams: {
            mealStatuses: mealStatusObjects,
            restrict,
            school_year: schoolYear || { id: schoolYearId },
        },
        chipText,
        tooltipData,
    })

    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        'meal_status',
        filter
    )

    const message = `Meal Status: **${chipText}**`
    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}

/**
 * Apply a tag filter from nlf-agent to Students 3D state.
 */
export async function applyTagFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    const tagIds = result.params?.tag_id || []
    const displayNames = result.params?.display_name || []
    const restrict = result.restrict !== undefined ? result.restrict : true
    const requireAll = result.params?.require_all || false
    const taggedAsOfDate = result.params?.tagged_as_of_date || null
    // Use the agent's school year if given, otherwise fall back to the search's school
    // year — tag data is year-scoped, same as assessment/attendance/disability.
    const schoolYearId = result.params?.school_year_id || context.params?.search?.school_year_id

    if (!tagIds.length) {
        msg.content = 'No tag IDs provided'
        return null
    }

    // Fetch full tag data to recover fields (e.g. color) needed by the checkbox panel
    const allTags = await fetchAndCacheTags(viewComponent)

    // If API fetch failed, abort rather than providing incomplete student data
    if (!allTags) {
        msg.content = 'Unable to apply tag filter at this time. Please try again in a moment, or contact support if the issue persists.'
        return null
    }

    // Match agent's selected IDs to full API objects
    const tagObjects = tagIds.map((id, idx) => {
        const apiObject = allTags.find(t => t.id === id || t.name === displayNames[idx])
        return apiObject || { id, name: displayNames[idx] || id }
    })

    // Fetch school year name if school_year_id is provided
    let schoolYear = null
    if (schoolYearId && viewComponent) {
        const schoolYears = await fetchAndCacheFilterData(
            viewComponent,
            'schoolYears',
            'schoolYear',
            {},
            (response) => response ?? []
        )
        if (schoolYears) {
            schoolYear = schoolYears.find(sy => sy.id === schoolYearId)
        }
    }

    const chipText = displayNames.join(', ')
    const uniqueId = Util.UUIDv4()

    const filter = {
        type: 'tag',
        id: uniqueId,
        restrict,
        report: true,
        params: {
            tag_id: tagIds,
            ...(taggedAsOfDate ? { tagged_as_of_date: taggedAsOfDate } : {}),
            ...(requireAll ? { require_all: true } : {}),
            ...(schoolYearId ? { school_year_id: schoolYearId } : {}),
        },
    }

    const tooltipData = {
        'Tags': chipText,
        'Restrict': restrict,
        'Require All': requireAll,
    }
    if (taggedAsOfDate) tooltipData['Tagged Before'] = taggedAsOfDate
    if (schoolYear?.name) tooltipData['School Year'] = schoolYear.name

    // Get reference to TagPanel if available
    const tagPanelRef = viewComponent?.$refs?.TagPanel?.[0] || null

    // Attach descriptor properties using the shared utility
    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: tagPanelRef || { load: () => {} },
        editorParams: {
            restrict,
            tags: tagObjects,
            tagged_as_of_date: taggedAsOfDate,
            require_all: requireAll,
            school_year: schoolYear || null,
        },
        chipText,
        tooltipData,
        grouper: {
            id: uniqueId,
            name: chipText,
            type: 'propertyArray',
            args: { path: `filters.${uniqueId}`, property: 'value' },
        }
    })

    // Note: We don't call load() here because tag allows multiple filters.
    // load() should only be called when editing an existing filter (via chip click),
    // not when creating a new filter, so the form stays blank for additional filters.

    // Replace in state
    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        'tag',
        filter
    )

    const message = `Tag: **${chipText}**`
    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}

/**
 * Whether an agent course option carries a teacher and at least one course. A teacher-only option
 * (from an ambiguous teacher match) does not: selecting it sends the conversation back to the agent.
 *
 * @param {Object} params - option params
 * @returns {boolean}
 */
export function isCourseOptionComplete(params) {
    return !!params && params.user_id != null && Array.isArray(params.course_id) && params.course_id.length > 0
}

// Ids can arrive as numbers (agent) or strings (API rows the manual panel keeps), so compare by value.
const sameId = (a, b) => a != null && b != null && Number(a) === Number(b)
const includesId = (list, id) => (list || []).some(x => sameId(x, id))

const SEARCH_SELECTIONS = {
    school: { field: 'schools', param: 'school_id', label: 'Schools' },
    grade: { field: 'grades', param: 'grade_id', label: 'Grades' },
}

/**
 * Whether a school or grade option from the agent is in the search bar's current selection.
 * @param {Object} searchBar - the demographic search component
 * @param {Object} option - agent option ({ id, name, kind })
 * @returns {boolean}
 */
export function isSearchOptionSelected(searchBar, option) {
    if (option?.kind === 'school_year') return sameId(searchBar?.schoolYear?.id, option.id)
    const config = SEARCH_SELECTIONS[option?.kind]
    if (!config) return false
    return (searchBar?.[config.field] || []).some(item => sameId(item.id, option.id))
}

/**
 * Add a school or grade option to the search bar's selection, or remove it when already selected,
 * like the search bar's own multiselect. Updates the search bar and returns what the search needs.
 *
 * @param {Object} searchBar - the demographic search component
 * @param {Object} option - agent option ({ id, name, kind })
 * @returns {Object|null} { param, ids, label, names }, or null when the option isn't a school or grade
 */
export function toggleSearchOption(searchBar, option) {
    const config = SEARCH_SELECTIONS[option?.kind]
    if (!config || !searchBar) return null

    const item = { ...option }
    delete item.kind
    const current = searchBar[config.field] || []
    const next = isSearchOptionSelected(searchBar, option)
        ? current.filter(selected => !sameId(selected.id, item.id))
        : [...current, item]

    // The search bar's watchers would refetch the active date; the agent flow keeps it as is.
    searchBar.skipActiveDateUpdate = true
    searchBar[config.field] = next.length ? next : null
    searchBar.$nextTick(() => {
        searchBar.skipActiveDateUpdate = false
    })

    return { param: config.param, ids: next.map(selected => selected.id), label: config.label, names: next.map(selected => selected.name) }
}

export async function selectSchoolYearOption(searchBar, option) {
    if (option?.kind !== 'school_year' || !searchBar) return null

    if (!sameId(searchBar.schoolYear?.id, option.id)) {
        // Hold the watcher off so the active date is fetched once, below
        searchBar.skipActiveDateUpdate = true
        try {
            searchBar.schoolYear = { id: option.id, name: option.name }
            await searchBar.$nextTick()
        } finally {
            searchBar.skipActiveDateUpdate = false
        }
        await searchBar.updateActiveDate()
    }

    const { school_year_id, student_active_date } = searchBar.getCurrentSearchParams()
    return { school_year_id, student_active_date, name: searchBar.schoolYear.name }
}

/**
 * Whether an agent course option is already applied: the same teacher, every course in the option, and
 * (when the option names periods) every one of those periods. Periods matter because the agent can offer
 * one button per period, all with identical courses.
 *
 * @param {Array} filters - currently applied Students 3D filters
 * @param {Object} option - agent option ({ type: 'course', params })
 * @returns {boolean}
 */
export function isCourseOptionApplied(filters, option) {
    const params = option?.params
    if (!isCourseOptionComplete(params)) return false

    const applied = (filters || []).find(f => f.type === 'course')
    if (!applied || !sameId(applied.params?.user_id, params.user_id)) return false

    if (!params.course_id.every(id => includesId(applied.params.course_id, id))) return false

    if (Array.isArray(params.period) && params.period.length) {
        const appliedPeriods = (applied.params.period || []).map(String)
        return params.period.every(period => appliedPeriods.includes(`${period}`))
    }
    return true
}

/**
 * Params for the course filter after clicking an agent course option, treating options like
 * checkboxes within one teacher. A filter belongs to one teacher, so an option for a different teacher
 * replaces the filter.
 *   - An option that names periods toggles those periods (courses stay as they are).
 *   - An option without periods toggles its courses. Removing courses keeps the applied periods; adding
 *     courses resets periods to all, since the new courses may meet in periods the old list lacks.
 *
 * @param {Array} filters - currently applied Students 3D filters
 * @param {Object} option - agent option ({ type: 'course', params })
 * @returns {Object|null} params to apply, or null when nothing remains (remove the filter)
 */
export function toggleCourseOption(filters, option) {
    const params = option.params
    const applied = (filters || []).find(f => f.type === 'course')
    if (!applied || !sameId(applied.params?.user_id, params.user_id)) return params

    const optionPeriods = (params.period || []).map(String)
    const appliedPeriods = (applied.params?.period || []).map(String)
    // editorParams is non-enumerable, so a copied or restored filter may not have it
    const selected = applied.editorParams?.selections?.course_id
    const appliedCourses = selected
        ? selected.map(c => ({ id: c.course_id, name: c.course_name }))
        : (applied.params?.course_id || []).map(id => ({ id, name: `${id}` }))
    const optionCourses = params.course_id.map((id, idx) => ({ id, name: params.course_name?.[idx] ?? `${id}` }))

    const build = (courses, periods) => ({
        user_id: params.user_id,
        user_name: params.user_name,
        course_id: courses.map(c => c.id),
        course_name: courses.map(c => c.name),
        ...(periods.length ? { period: periods } : {}),
    })

    if (isCourseOptionApplied(filters, option)) {
        if (optionPeriods.length) {
            const remainingPeriods = appliedPeriods.filter(p => !optionPeriods.includes(p))
            return remainingPeriods.length ? build(appliedCourses, remainingPeriods) : null
        }
        const remaining = appliedCourses.filter(c => !includesId(params.course_id, c.id))
        return remaining.length ? build(remaining, appliedPeriods) : null
    }

    const courses = [...appliedCourses, ...optionCourses.filter(c => !includesId(appliedCourses.map(a => a.id), c.id))]
    const periods = optionPeriods.length ? [...new Set([...appliedPeriods, ...optionPeriods])] : []
    return build(courses, periods)
}

/**
 * Apply a course filter from nlf-agent to Students 3D state, in the shape the manual CoursePanel
 * emits (course_id, period and user_id only — the school year just scopes the panel's dropdowns).
 * Periods the agent omits mean all periods, which the panel also selects by default.
 */
export async function applyCourseFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    const userId = result.params?.user_id
    const userName = result.params?.user_name || ''
    const courseIds = result.params?.course_id || []
    const courseNames = result.params?.course_name || []
    const agentPeriods = result.params?.period || []

    if (userId == null || !courseIds.length) {
        msg.content = 'A teacher and at least one course are required for a course filter'
        return null
    }

    const schoolYearId = context.params?.search?.school_year_id
        || viewComponent?.$refs?.demographicSearch?.getCurrentSearchParams?.()?.school_year_id
    if (!schoolYearId) {
        msg.content = 'School year ID is required for course filters'
        return null
    }

    // Same endpoint and params the panel uses to list a teacher's periods. Only needed when the agent didn't
    // name periods: omitted means all of them, which the panel also selects by default.
    let periods = agentPeriods.map(p => `${p}`)
    if (!periods.length) {
        let allPeriods
        try {
            const response = await viewComponent.$axios.get(
                `courseHistory.php?action=get&property=period&school_year_id=${schoolYearId}&user_id=${userId}&course_id=${courseIds.join(',')}`
            )
            allPeriods = (response?.data?.courses || []).map(p => `${p.period}`)
        } catch (error) {
            console.error('Error fetching course periods:', error)
            allPeriods = null
        }

        // Abort rather than apply a filter that may select the wrong students
        if (!allPeriods) {
            msg.content = 'Unable to apply course filter at this time. Please try again in a moment, or contact support if the issue persists.'
            return null
        }
        periods = allPeriods
    }

    if (!periods.length) {
        msg.content = 'No periods were found for those courses, so the course filter was not applied.'
        return null
    }

    const courseObjects = courseIds.map((id, idx) => ({ course_id: id, course_name: courseNames[idx] || `${id}` }))
    const chipText = courseObjects.map(c => c.course_name).join(', ')
    const uniqueId = Util.UUIDv4()

    const filter = {
        type: 'course',
        id: uniqueId,
        restrict: true,
        report: false,
        params: {
            course_id: courseIds,
            period: periods,
            user_id: userId,
        },
    }

    // CoursePanel lives in MoreFiltersPanel
    const moreFiltersRef = viewComponent?.$refs?.MoreFiltersPanel?.[0]
    const coursePanelRef = moreFiltersRef?.$refs?.coursePanel

    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: coursePanelRef,
        // Must match CoursePanel's selections shape so the chip reopens the panel correctly
        editorParams: {
            selections: {
                advancedFilters: { student_active_flag: { id: 1 } },
                cohort_school_year_id: schoolYearId,
                user_id: { user_id: userId, user_full_name: userName },
                course_id: courseObjects,
                period: periods.map(period => ({ period })),
            },
        },
        chipText,
        tooltipData: { Course: chipText, Period: periods.join(', '), ...(userName ? { Teacher: userName } : {}) },
    })

    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        'course',
        filter
    )

    const message = `Course: **${chipText}**${userName ? ` (Teacher: ${userName})` : ''}`
    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}

/**
 * Encode a custom demographic's selected option(s) into the comma-joined string
 * AdvancedDemographicFilter.vue expects (e.g. "45,67" or "!45" to exclude).
 *
 * Expands each selected option to its full display_name_group — the agent's search_demographics
 * result already deduplicated options down to one representative id per display_name (same
 * pattern as gender/ethnicity), so a raw value sharing that display_name but under a different
 * id would otherwise be silently excluded from the match. Mirrors
 * FilterPanelUtil.collectIdsFromSelection's expansion used for gender/ethnicity.
 *
 * @param {Object} demographic - full demographic record (with .options) from the demographics store
 * @param {Object} params - agent's filter params (option_id[], exclude[])
 * @returns {string|null} encoded value, or null if no valid option_id matched this demographic
 */
function encodeDemographicOptionValue(demographic, params) {
    const optionIds = params.option_id || []
    if (!optionIds.length) return null

    const excludeFlags = params.exclude || []
    const options = demographic.options || []
    const seenTokens = new Set()
    const parts = []

    optionIds.forEach((id, idx) => {
        const option = options.find(o => o.id === id)
        // Agent proposed an id that doesn't exist for this demographic (stale data, race with an
        // admin edit) — skip rather than encode something that doesn't correspond to real data.
        if (!option) return

        const exclude = !!excludeFlags[idx]
        const expandedIds = FilterPanelUtil.collectIdsFromSelection([option], { idKey: 'id', parseInteger: true })
        expandedIds.forEach((expandedId) => {
            const token = exclude ? `!${expandedId}` : `${expandedId}`
            if (!seenTokens.has(token)) {
                seenTokens.add(token)
                parts.push(token)
            }
        })
    })

    return parts.length ? parts.join(',') : null
}

/**
 * Encode a non-"option" demographic value into the "[operator]:[value]" (or
 * "[operator]:[value]:[value2]" for "bt") string AdvancedDemographicFilter.vue expects.
 *
 * @param {Object} params - agent's filter params (operator, value, value2)
 * @returns {string|null} encoded value, or null if operator/value are missing/invalid
 */
function encodeDemographicScalarValue(params) {
    const { operator, value, value2 } = params
    if (!operator || value == null) return null
    if (operator === 'bt') {
        if (value2 == null) return null
        return `${operator}:${value}:${value2}`
    }
    return `${operator}:${value}`
}

/**
 * Flag a demographic filter whose school year was filled in from the search rather than chosen,
 * so executeSearch() keeps it in step when the search year changes. A year the agent or user
 * picked explicitly is never flagged, so it stays put. Non-enumerable so it never reaches the
 * request payload.
 *
 * @param {Object} filter - applied Students 3D filter
 */
export function markSchoolYearFollowsSearch(filter) {
    Object.defineProperty(filter, 'schoolYearFollowsSearch', { value: true, writable: true, configurable: true })
}

/**
 * Whether an agent demographic option carries enough to build a filter from. A field-only
 * option (just id, name and type, e.g. from a "which fields can I filter by?" answer) does not.
 *
 * @param {Object} params - option params
 * @returns {boolean}
 */
export function isDemographicOptionComplete(params) {
    if (!params) return false
    if (Array.isArray(params.option_id) && params.option_id.length) return true
    return encodeDemographicScalarValue(params) !== null
}

/**
 * Whether an agent-provided demographic option (an ambiguous/partial option button) is already
 * applied. Applied demographic filters are keyed `demo_<demographic_id>`, not `demographic`, so
 * the generic "same type" matching used for other filters can't find them.
 *
 * Compares the value too, not just the demographic: with "Yes" applied, the "No" button must
 * read as unselected so clicking it switches the filter instead of removing it.
 *
 * @param {Array} filters - currently applied Students 3D filters
 * @param {Object} option - agent option ({ type: 'demographic', params })
 * @returns {boolean}
 */
export function isDemographicOptionApplied(filters, option) {
    const params = option?.params
    if (!params || params.demographic_id == null) return false

    const key = `demo_${params.demographic_id}`
    const applied = (filters || []).find(f => f.type === key)
    const appliedValue = applied?.params?.[key]
    if (appliedValue == null) return false

    if (Array.isArray(params.option_id) && params.option_id.length) {
        const excludeFlags = params.exclude || []
        // Applied ids are already expanded to full display_name_groups, so the option's own id
        // is always among them when it's applied.
        const appliedTokens = `${appliedValue}`.split(',').map(s => s.trim())
        return params.option_id.every((id, idx) => appliedTokens.includes(excludeFlags[idx] ? `!${id}` : `${id}`))
    }

    return encodeDemographicScalarValue(params) === appliedValue
}

/**
 * Build the id/value map AdvancedDemographicFilter.vue's grouper reads from
 * (filters.<uniqueId>) for "option"-type demographics. Ported verbatim from
 * DemographicsPanel.vue's displayNamesWithIds() so the manual panel and the agent-driven path
 * produce byte-for-byte identical filter shapes — required for "edit this filter" (chip click)
 * to reopen the panel correctly.
 *
 * @param {Object} demographic - full demographic record (with .options)
 * @param {string} encoded - the encoded option value string (e.g. "45,!67")
 * @returns {Object} map like {0: {ids, value}, 1: {ids, value}, ...}
 */
function buildDemographicOptionValuesById(demographic, encoded) {
    const options = demographic?.options
    if (!options?.length || !encoded) return {}

    const parts = `${encoded}`.split(',').map(s => s.trim()).filter(Boolean)
    const byKey = new Map()

    parts.forEach((part) => {
        const idStr = part[0] === '!' ? part.slice(1) : part
        let option = options.find(o => String(o.id) === String(idStr))
        if (!option) {
            const member = options.find(
                o => o.display_name_group && o.display_name_group.some(g => String(g) === String(idStr))
            )
            if (member?.display_name_group?.length) {
                option = options.find(o => String(o.id) === String(member.display_name_group[0])) || member
            }
        }
        if (!option) return
        const key = option.display_name_group ? `g:${option.display_name_group[0]}` : `i:${option.id}`
        if (byKey.has(key)) return
        byKey.set(key, { ids: option.display_name_group || option.id, value: option.display_name || option.value })
    })

    const demo = {}
    let idx = 0
    byKey.forEach((val) => { demo[idx++] = val })
    return demo
}

/**
 * Apply a custom demographic filter from nlf-agent to Students 3D state.
 *
 * Every demographic is entirely district-defined — the agent only names WHICH demographic
 * (demographic_id, from search_demographics) and WHAT to match. This resolves demographic_id
 * back to its full definition (value_type + options) via the same store DemographicsPanel/
 * FilterChips use, encodes the value the same way AdvancedDemographicFilter.vue does, and builds
 * a filter object matching DemographicsPanel.emit()'s shape so editing an applied filter (chip
 * click) reopens the panel correctly.
 *
 * Unlike gender/ethnicity/disability/tag, several demographic filters can coexist — each
 * demographic gets its own filter type (`demo_<id>`), so replaceFilterInState below only ever
 * replaces THIS demographic's prior filter, never a different demographic's.
 */
export async function applyDemographicFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    const params = result.params || {}
    const demographicId = params.demographic_id
    const restrict = result.restrict !== undefined ? result.restrict : true

    if (demographicId == null) {
        msg.content = 'No demographic specified'
        return null
    }

    // Resolve via the same store the manual panel/chips use — can_search-filtered, so a
    // demographic the current viewer isn't permitted to search by can never build a filter here,
    // even if it were somehow proposed (e.g. stale agent-side cache).
    if (!viewComponent?.$store) {
        msg.content = 'Unable to apply demographic filter at this time. Please try again in a moment, or contact support if the issue persists.'
        return null
    }
    await viewComponent.$store.dispatch('global/loadDemographics', { ifEmpty: true })
    const demographic = viewComponent.$store.getters['global/demographicsSearchable']
        .find(d => d.id === demographicId)

    if (!demographic) {
        msg.content = 'Unable to apply demographic filter at this time. Please try again in a moment, or contact support if the issue persists.'
        return null
    }

    const encoded = demographic.value_type === 'option'
        ? encodeDemographicOptionValue(demographic, params)
        : encodeDemographicScalarValue(params)

    if (encoded == null) {
        msg.content = `Unable to build a filter for "${demographic.name}" from the given values`
        return null
    }

    const key = `demo_${demographic.id}`
    const uniqueId = Util.UUIDv4()
    const requireAll = demographic.value_type === 'option' && !!params.require_all
    // Use the agent's school year if given, otherwise fall back to the search's school year. On a
    // first search params.search can still be empty when the filter is applied, so the search bar
    // itself is the last resort (same as the disability filter).
    const searchBar = viewComponent?.$refs?.demographicSearch
    const schoolYearId = params.school_year_id
        || context.params?.search?.school_year_id
        || searchBar?.getCurrentSearchParams?.()?.school_year_id
        || null

    const optionValuesById = demographic.value_type === 'option'
        ? buildDemographicOptionValuesById(demographic, encoded)
        : null

    const filter = {
        type: key,
        id: uniqueId,
        restrict,
        report: true,
        params: {
            [key]: encoded,
            ...(optionValuesById && Object.keys(optionValuesById).length ? { [uniqueId]: optionValuesById } : {}),
            ...(requireAll ? { require_all: true } : {}),
            ...(schoolYearId ? { school_year_id: schoolYearId } : {}),
        },
    }

    // Same cosmetic parse the manual panel uses to build its tooltip text — best-effort, since
    // this is display-only and shouldn't block applying the filter if it fails.
    let demographicResult = null
    if (typeof viewComponent?.$parseDemographic === 'function') {
        try {
            demographicResult = await viewComponent.$parseDemographic(key, encoded)
        } catch (error) {
            console.error('Error parsing demographic result:', error)
        }
    }

    let schoolYear = null
    if (schoolYearId) {
        // The search bar already holds the selected year (id + name); only look it up if the
        // filter is for a different year than the one showing there.
        if (searchBar?.schoolYear?.id === schoolYearId) {
            schoolYear = { ...searchBar.schoolYear }
        } else {
            const schoolYears = await fetchAndCacheFilterData(
                viewComponent,
                'schoolYears',
                'schoolYear',
                {},
                (response) => response ?? []
            )
            if (schoolYears) {
                schoolYear = schoolYears.find(sy => sy.id === schoolYearId)
            }
        }
    }

    const chipText = demographic.name

    const tooltipData = {
        'Demographic': chipText,
        'Params': demographicResult?.textWithOp,
        'Restrict': restrict,
    }
    if (demographic.value_type === 'option') tooltipData['Require All'] = requireAll
    if (schoolYear?.name) tooltipData['School Year'] = schoolYear.name
    // The filter's descriptor props are non-enumerable (kept out of the request), so Vue never sees
    // them change. Making this object reactive lets executeSearch() update the year in place and
    // have the chip tooltip redraw, instead of showing the year from when the filter was applied.
    Vue.observable(tooltipData)

    const demographicPanelRef = viewComponent?.$refs?.DemographicsPanel?.[0] || null

    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: demographicPanelRef || { load: () => {} },
        editorParams: {
            restrict,
            demographic: { ...demographic },
            demographicParams: encoded,
            demographicResult,
            require_all: requireAll,
            school_year: schoolYear || null,
        },
        chipText,
        tooltipData,
        grouper: {
            id: uniqueId,
            name: chipText,
            type: 'propertyArray',
            args: { path: `filters.${uniqueId}`, property: 'value' },
        },
    })

    // A year taken from the search (not given by the agent) should keep following the search.
    if (schoolYearId && !params.school_year_id) {
        markSchoolYearFollowsSearch(filter)
    }

    // Note: we don't call load() here — several distinct demographics may coexist as separate
    // filters, so applying one shouldn't populate the form for the next (same rationale as tag).

    // Replace only THIS demographic's existing filter — key is demo_<id>, distinct per
    // demographic, so a different demographic's filter is never touched.
    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        key,
        filter
    )

    const message = `Demographic: **${chipText}**`
    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}

/**
 * Apply an attendance filter from nlf-agent to Students 3D state.
 */
export async function applyAttendanceFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    const restrict = result.restrict !== undefined ? result.restrict : true
    const inputParams = result.params || {}

    // Extract attendance params
    const rate_type = inputParams.rate_type
    const filter_by = inputParams.filter_by || 'rate'
    const attendance_value_min = inputParams.attendance_value_min ?? 0
    const attendance_value_max = inputParams.attendance_value_max ?? (filter_by === 'rate' ? 100 : 0)
    const occurrence_month_num = inputParams.occurrence_month_num || []
    const occurrence_weekday_num = inputParams.occurrence_weekday_num || []
    const min_possible_attendance_days = inputParams.min_possible_attendance_days || ''
    // Use the agent's school year if given, otherwise fall back to the search's school
    // year — attendance data is year-scoped, same as assessment/disability/tag.
    const schoolYearId = inputParams.school_year_id || context.params?.search?.school_year_id

    // Fetch school year name if school_year_id is provided
    let schoolYear = null
    if (schoolYearId && viewComponent) {
        const schoolYears = await fetchAndCacheFilterData(
            viewComponent,
            'schoolYears',
            'schoolYear',
            {},
            (response) => response ?? []
        )
        if (schoolYears) {
            schoolYear = schoolYears.find(sy => sy.id === schoolYearId)
        }
    }

    // Build filter params
    const filterParams = {
        filter_by,
        attendance_value_min,
        attendance_value_max,
    }

    // Add rate_type, defaulting to SIS Reported if not specified
    filterParams.rate_type = rate_type ?? 'days_equivalent'

    // Add optional params (only if present)
    if (schoolYearId) filterParams.school_year_id = schoolYearId
    if (occurrence_month_num.length) filterParams.occurrence_month_num = occurrence_month_num
    if (occurrence_weekday_num.length) filterParams.occurrence_weekday_num = occurrence_weekday_num
    if (min_possible_attendance_days) filterParams.min_possible_attendance_days = min_possible_attendance_days

    const uniqueId = Util.UUIDv4()

    // Build chip text
    const minLabel = filter_by === 'rate' ? `${attendance_value_min}%` : `${attendance_value_min} Days Missed`
    const maxLabel = filter_by === 'rate' ? `${attendance_value_max}%` : `${attendance_value_max} Days Missed`

    let chipText = `Attendance: ${minLabel}-${maxLabel}`

    if (occurrence_month_num.length > 0) {
        const monthNames = occurrence_month_num.map(v => {
            const month = MONTHS.find(m => m.value === v)
            return month?.name || `Month ${v}`
        }).join(', ')
        chipText += ` (${monthNames})`
    } else if (occurrence_weekday_num.length > 0) {
        const dayNames = occurrence_weekday_num.map(v => {
            const weekday = WEEKDAYS.find(w => w.value === v)
            return weekday?.name || `Day ${v}`
        }).join(', ')
        chipText += ` (${dayNames})`
    }

    const filter = {
        type: 'attendance',
        id: uniqueId,
        restrict,
        report: true,
        params: filterParams,
    }

    // Get reference to AttendancePanel if available
    const attendancePanelRef = viewComponent?.$refs?.AttendancePanel?.[0] || null

    // Build editorParams for panel syncing
    const editorParams = {
        restrict,
        school_year: schoolYear || null,
        occurrence_month_num: occurrence_month_num.map(v => {
            const month = MONTHS.find(m => m.value === v)
            return month || { name: `Month ${v}`, value: v }
        }),
        occurrence_weekday_num: occurrence_weekday_num.map(v => {
            const weekday = WEEKDAYS.find(w => w.value === v)
            return weekday || { name: `Day ${v}`, value: v }
        }),
        attendance_value_min,
        attendance_value_max,
        rate_type: rate_type ? { name: RATE_TYPES[rate_type] || rate_type, value: rate_type } : null,
        filter_by,
        min_possible_attendance_days,
    }

    // Build tooltip data
    const minProp = filter_by === 'rate' ? 'Min Rate' : 'Min Days Missed'
    const maxProp = filter_by === 'rate' ? 'Max Rate' : 'Max Days Missed'
    const tooltipData = {
        [minProp]: minLabel,
        [maxProp]: maxLabel,
        'Restrict': restrict,
    }
    if (occurrence_month_num.length > 0) {
        tooltipData['Month(s)'] = occurrence_month_num.map(v => {
            const month = MONTHS.find(m => m.value === v)
            return month?.name || `Month ${v}`
        }).join(', ')
    }
    if (occurrence_weekday_num.length > 0) {
        tooltipData['Weekday(s)'] = occurrence_weekday_num.map(v => {
            const weekday = WEEKDAYS.find(w => w.value === v)
            return weekday?.name || `Day ${v}`
        }).join(', ')
    }
    tooltipData['Rate Type'] = RATE_TYPES[filterParams.rate_type] || filterParams.rate_type
    if (min_possible_attendance_days) tooltipData['Min Days Enrolled'] = min_possible_attendance_days
    if (schoolYear?.name) tooltipData['School Year'] = schoolYear.name

    // Attach descriptor properties using the shared utility
    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: attendancePanelRef || { load: () => {} },
        editorParams,
        chipText,
        tooltipData,
        grouper: {
            id: uniqueId,
            name: chipText,
            type: 'controller',
            args: { property: `filters.${uniqueId}.value` }
        }
    })

    // Note: We don't call load() here because attendance allows multiple filters.
    // load() should only be called when editing an existing filter (via chip click),
    // not when creating a new filter, so the form stays blank for additional filters.

    // Replace in state
    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        'attendance',
        filter
    )

    const message = `Attendance: **${minLabel}**-**${maxLabel}**`
    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}

/**
 * Apply an incident filter from nlf-agent to Students 3D state.
 */
export async function applyIncidentFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers) {
        msg.content = 'Invalid context provided'
        return null
    }

    const restrict = result.restrict !== undefined ? result.restrict : true
    const inputParams = result.params || {}

    // If _editorObjects is provided (from merge/remove operations), use those directly
    // Otherwise, extract from inputParams (from agent)
    let incident_behavior_type, incident_behavior, incident_location, incident_response

    if (result._editorObjects) {
        incident_behavior_type = result._editorObjects.incident_behavior_type || []
        incident_behavior = result._editorObjects.incident_behavior || []
        incident_location = result._editorObjects.incident_location || []
        incident_response = result._editorObjects.incident_response || []
    } else {
        // Build from agent-provided IDs and names
        const incident_behavior_type_id = inputParams.incident_behavior_type_id || []
        const incident_behavior_type_name = inputParams.incident_behavior_type_name || []
        const incident_behavior_id = inputParams.incident_behavior_id || []
        const incident_behavior_name = inputParams.incident_behavior_name || []
        const incident_location_id = inputParams.incident_location_id || []
        const incident_location_name = inputParams.incident_location_name || []
        const incident_response_id = inputParams.incident_response_id || []
        const incident_response_name = inputParams.incident_response_name || []

        // Build object arrays for multiselect fields
        const buildObjectArray = (ids, names) => {
            if (!ids?.length) return []
            return ids.map((id, idx) => ({ id, name: names[idx] || String(id) }))
        }

        incident_behavior_type = buildObjectArray(incident_behavior_type_id, incident_behavior_type_name)
        incident_behavior = buildObjectArray(incident_behavior_id, incident_behavior_name)
        incident_location = buildObjectArray(incident_location_id, incident_location_name)
        incident_response = buildObjectArray(incident_response_id, incident_response_name)
    }
    const start_date = inputParams.start_date || result._editorObjects?.start_date || ''
    const end_date = inputParams.end_date || result._editorObjects?.end_date || ''
    const minimum_count = inputParams.minimum_count || result._editorObjects?.minimum_count || ''
    const maximum_count = inputParams.maximum_count || result._editorObjects?.maximum_count || ''

    // Use the agent's school year if given, otherwise fall back to the search's school
    // year — incident data is year-scoped, same as assessment/disability/tag.
    const schoolYearId = inputParams.school_year_id || context.params?.search?.school_year_id

    // Fetch school year name if school_year_id is available
    let schoolYear = result._editorObjects?.school_year || null
    if (!schoolYear && schoolYearId && viewComponent) {
        const schoolYears = await fetchAndCacheFilterData(
            viewComponent,
            'schoolYears',
            'schoolYear',
            {},
            (response) => response ?? []
        )
        if (schoolYears) {
            schoolYear = schoolYears.find(sy => sy.id === schoolYearId)
        }
    }

    // Build filter params - only IDs and scalar values (for API)
    const filterParams = {
        incident_behavior_type_id: incident_behavior_type.map(x => x.id),
        incident_behavior_id: incident_behavior.map(x => x.id),
        incident_location_id: incident_location.map(x => x.id),
        incident_response_id: incident_response.map(x => x.id),
        start_date,
        end_date,
        minimum_count,
        maximum_count,
        school_year_id: schoolYearId
    }

    // Reuse existing filter ID if provided (for merge operations), otherwise generate new
    const uniqueId = result.id || Util.UUIDv4()

    // Helper to check if value is blank
    const isBlank = (value) => {
        return value === null || value === undefined || value === '' ||
               (Array.isArray(value) && value.length === 0)
    }

    // Helper to pick non-blank values
    const pickBy = (obj, predicate) => {
        return Object.keys(obj).reduce((result, key) => {
            if (predicate(obj[key])) {
                result[key] = obj[key]
            }
            return result
        }, {})
    }

    const filter = {
        type: 'incident',
        id: uniqueId,
        restrict,
        report: true,
        params: pickBy(filterParams, x => !isBlank(x))
    }

    // Build chip text showing all applied fields
    const chipParts = []
    if (incident_behavior_type?.length) {
        chipParts.push(`Type: ${incident_behavior_type.map(x => x.name).join(', ')}`)
    }
    if (incident_behavior?.length) {
        chipParts.push(`Code: ${incident_behavior.map(x => x.name).join(', ')}`)
    }
    if (incident_location?.length) {
        chipParts.push(`Location: ${incident_location.map(x => x.name).join(', ')}`)
    }
    if (incident_response?.length) {
        chipParts.push(`Response: ${incident_response.map(x => x.name).join(', ')}`)
    }
    if (start_date || end_date) {
        const dateRange = [start_date, end_date].filter(Boolean).join(' to ')
        chipParts.push(`Date: ${dateRange}`)
    }

    const chipText = chipParts.length > 0
        ? `Incident\n${chipParts.join('\n')}`
        : 'Incident'

    // Get reference to IncidentPanel if available
    const incidentPanelRef = viewComponent?.$refs?.IncidentPanel?.[0] || null

    // Build editorParams for panel syncing
    const editorParams = {
        restrict,
        school_year: schoolYear || null,
        start_date,
        end_date,
        incident_behavior_type,
        incident_behavior,
        incident_location,
        incident_response,
        minimum_count,
        maximum_count
    }

    // Build tooltip data
    const tooltipData = {
        'Incidents After': start_date,
        'Incidents Before': end_date,
        'Incident Type': incident_behavior_type?.map(x => x.name).join(', '),
        'Code': incident_behavior?.map(x => x.name).join(', '),
        'Location': incident_location?.map(x => x.name).join(', '),
        'Response': incident_response?.map(x => x.name).join(', '),
        'Minimum': minimum_count,
        'Maximum': maximum_count,
        'Restrict': restrict,
        'School Year': schoolYear?.name
    }

    // Attach descriptor properties
    FilterPanelUtil.attachFilterDescriptor(filter, {
        editor: incidentPanelRef || { load: () => {} },
        editorParams,
        chipText,
        tooltipData: pickBy(tooltipData, x => !isBlank(x)),
        grouper: {
            id: uniqueId,
            name: chipText,
            type: 'oldEvenRange',
            args: { property: `filters.${uniqueId}.value` }
        }
    })

    // Note: We don't call load() here because incident allows multiple filters.
    // load() should only be called when editing an existing filter (via chip click),
    // not when creating a new filter, so the form stays blank for additional filters.

    // Replace in state (removes all incident filters and adds this new one)
    const { filters, groupers } = replaceFilterInState(
        context.params.filters,
        context.groupers,
        'incident',
        filter
    )

    const message = `Incident: **${chipText.replace('Incident\n', '')}**`

    return { filter, updatedFilters: filters, updatedGroupers: groupers, message }
}


/**
 * Apply an assessment filter from nlf-agent to Students 3D state.
 * Builds the filter structure that matches what AssessmentPanel creates manually.
 *
 * @param {Object} result - nlf-agent result containing assessment filter params
 * @param {Object} msg - chat message to update with success/error content
 * @param {Object} context - { params, groupers, modelGet } for updating Students 3D state and making API calls
 * @param {Object} viewComponent - Vue component reference to access AssessmentPanel
 * @returns {Promise<Object>} { filter, updatedFilters, updatedGroupers, message } - the constructed filter, updated state arrays, and notification message
 */
export async function applyAssessmentFilter(result, msg, context, viewComponent) {
    if (!context || !context.params || !context.groupers || !context.modelGet) {
        msg.content = 'Invalid context provided'
        return null
    }

    try {
        const { params, groupers, modelGet } = context
        const inputParams = result.params || result

        // Extract assessment data from the new object format
        const dataPointType = inputParams.data_point_type
        const subCategory = inputParams.sub_category
        const dataPointName = inputParams.data_point_name
        const scoreDetailType = inputParams.score_detail_type || null
        const targetSet = inputParams.target_set || null
        const targetDescriptors = Array.isArray(inputParams.target_descriptor) ? inputParams.target_descriptor : null

        // Validate required fields
        if (!dataPointType || !subCategory || !dataPointName) {
            msg.content = 'Assessment filter requires data_point_type, sub_category, and data_point_name objects'
            return null
        }

        const scoreFilter = inputParams?.assessment_params?.score_filter || result?.assessment_params?.score_filter || null

        let minimumScore = inputParams?.minimum_score || result?.minimum_score
        let maximumScore = inputParams?.maximum_score || result?.maximum_score

        // Parse score_filter if present (new BE format)
        if (scoreFilter && typeof scoreFilter === 'object') {
            const operator = String(scoreFilter.operator || '').toLowerCase()
            const rawValue = scoreFilter.value
            if (operator === 'gte' || operator === 'gt') {
                minimumScore = toNumberOrNull(rawValue)
            } else if (operator === 'lte' || operator === 'lt') {
                maximumScore = toNumberOrNull(rawValue)
            } else if (operator === 'between' && Array.isArray(rawValue) && rawValue.length >= 2) {
                minimumScore = toNumberOrNull(rawValue[0])
                maximumScore = toNumberOrNull(rawValue[1])
            } else if (operator === 'eq') {
                const eqValue = toNumberOrNull(rawValue)
                minimumScore = eqValue
                maximumScore = eqValue
            }
        }

        // Validate IDs by checking if they exist in the database and matching by name if needed
        // This handles cases where agent sends only names or database IDs have changed
        let validatedDataPointType = { ...dataPointType }
        let validatedSubCategory = { ...subCategory }
        let validatedDataPointName = { ...dataPointName }

        // Validate dataPointType
        const dataPointTypeList = await modelGet('assessment', { hidden: 0 })
        let match = dataPointTypeList?.find(x => x?.id === dataPointType?.id)
        if (!match) {
            match = dataPointTypeList?.find(x => x?.name === dataPointType?.name)
        }
        if (match) {
            validatedDataPointType = { id: match.id, name: match.name }
        } else {
            msg.content = `Assessment group "${dataPointType?.name || 'Unknown'}" not found in database`
            return null
        }

        // Validate subCategory
        const subCategoryList = await modelGet('subcategory', {
            data_point_type_id: validatedDataPointType.id,
            hidden: 0
        })
        match = subCategoryList?.find(x => x?.id === subCategory?.id)
        if (!match) {
            match = subCategoryList?.find(x => x?.name === subCategory?.name)
        }
        if (match) {
            validatedSubCategory = { id: match.id, name: match.name, display_name: match.display_name }
        } else {
            msg.content = `Assessment "${subCategory?.name || 'Unknown'}" not found for group "${validatedDataPointType.name}"`
            return null
        }

        // Validate dataPointName
        const dataPointNameList = await modelGet('dataPointName', {
            data_point_type_id: validatedDataPointType.id,
            sub_category_id: validatedSubCategory.id,
            hidden: 0
        })
        match = dataPointNameList?.find(x => x?.id === dataPointName?.id)
        if (!match) {
            // ID doesn't exist - try matching by name
            match = dataPointNameList?.find(x => x?.name === dataPointName?.name || x?.alias === dataPointName?.name)
        }
        if (match) {
            // Use the database object to ensure we have all properties (including alias)
            validatedDataPointName = { id: match.id, name: match.name, alias: match.alias || match.name }
        } else {
            msg.content = `Time window "${dataPointName?.name || 'Unknown'}" not found for assessment "${validatedSubCategory.name}"`
            return null
        }

        // Fetch alpha maps for score conversion
        const alphaMapResponse = await modelGet('alphaMap', {
            data_point_type_id: validatedDataPointType.id,
            sub_category_id: validatedSubCategory.id,
        })
        const alphaMaps = alphaMapResponse?.data?.alpha_maps || []
        const minimumScoreEditor = toAssessmentScoreEditorValue(minimumScore, alphaMaps)
        const maximumScoreEditor = toAssessmentScoreEditorValue(maximumScore, alphaMaps)

        const uniqueId = Util.UUIDv4()
        // Use the agent's school year if given, otherwise fall back to the search's school
        // year — assessment data is year-scoped, same as attendance/disability/tag.
        const schoolYearId = inputParams.school_year_id || params?.search?.school_year_id

        // Fetch the school year name to populate the editor's form state.
        let schoolYear = null
        if (schoolYearId && viewComponent) {
            const schoolYears = await fetchAndCacheFilterData(
                viewComponent,
                'schoolYears',
                'schoolYear',
                {},
                (response) => response ?? []
            )
            if (schoolYears) {
                schoolYear = schoolYears.find(sy => sy.id === schoolYearId) || null
            }
        }

        // Validate score detail type (optional)
        let validatedScoreDetailType = null
        if (scoreDetailType) {
            const scoreDetailTypeList = await modelGet('scoreDetailType', { data_point_type_id: validatedDataPointType.id })
            let sdMatch = scoreDetailTypeList?.find(x => x?.id === scoreDetailType?.id)
            if (!sdMatch) {
                sdMatch = scoreDetailTypeList?.find(x => x?.code === scoreDetailType?.code || x?.description === scoreDetailType?.description)
            }
            if (sdMatch) {
                validatedScoreDetailType = { id: sdMatch.id, code: sdMatch.code, description: sdMatch.description }
            } else {
                msg.content = `Score detail type "${scoreDetailType?.description || scoreDetailType?.code || 'Unknown'}" not found for assessment group "${validatedDataPointType.name}"`
                return null
            }
        }

        // Validate performance band / target set and its descriptors (optional)
        let validatedTargetSet = null
        let validatedTargetDescriptors = null
        if (targetSet) {
            const userTargetSets = await modelGet('userTargetSet')
            const bandsForGroup = (userTargetSets || [])
                .filter(t => t?.id === validatedDataPointType.id)
                .flatMap(t => (t.schoolYears || [])
                    .filter(year => !year.id || year.id === schoolYearId)
                    .flatMap(year => year.targetSets || []))
            let tsMatch = bandsForGroup.find(x => x?.id === targetSet?.id)
            if (!tsMatch) {
                tsMatch = bandsForGroup.find(x => x?.name === targetSet?.name)
            }
            if (!tsMatch) {
                msg.content = `Performance band "${targetSet?.name || 'Unknown'}" not found for assessment group "${validatedDataPointType.name}"`
                return null
            }
            validatedTargetSet = { id: tsMatch.id, name: tsMatch.name }

            if (targetDescriptors?.length) {
                const availableDescriptors = await modelGet('targetSetDescriptor', {
                    target_set_id: validatedTargetSet.id,
                    types: '1,3',
                }) || []
                validatedTargetDescriptors = targetDescriptors
                    .map(d => availableDescriptors.find(x => x.id === d.id) || availableDescriptors.find(x => x.name === d.name))
                    .filter(Boolean)
                    .map(d => ({ id: d.id, name: d.name, rank: d.rank }))
                if (!validatedTargetDescriptors.length) {
                    msg.content = `Target descriptor not found for performance band "${validatedTargetSet.name}"`
                    return null
                }
            }
        }

        // Build filter params, omitting blank values
        const filterParams = {}
        if (validatedDataPointType.id) filterParams.data_point_type_id = validatedDataPointType.id
        if (validatedSubCategory.id) filterParams.sub_category_id = validatedSubCategory.id
        if (validatedDataPointName.id) filterParams.data_point_name_id = validatedDataPointName.id
        if (schoolYearId) filterParams.school_year_id = schoolYearId

        const minScoreValue = getScoreParamValue(minimumScoreEditor)
        const maxScoreValue = getScoreParamValue(maximumScoreEditor)
        if (minScoreValue !== null) filterParams.minimum_score = minScoreValue
        if (maxScoreValue !== null) filterParams.maximum_score = maxScoreValue
        if (validatedScoreDetailType?.id) filterParams.score_detail_type_id = validatedScoreDetailType.id
        if (validatedTargetSet?.id) filterParams.target_set_id = validatedTargetSet.id
        if (validatedTargetDescriptors?.length) filterParams.target_descriptor_id = validatedTargetDescriptors.map(d => d.id)

        const filter = {
            type: 'assessment',
            id: uniqueId,
            restrict: true,
            report: true,
            params: filterParams,
        }

        // Get reference to AssessmentPanel if available
        const assessmentPanelRef = viewComponent?.$refs?.AssessmentPanel?.[0] || null

        // Attach descriptor properties
        FilterPanelUtil.attachFilterDescriptor(filter, {
            editor: assessmentPanelRef || { load: () => {} }, // Use actual AssessmentPanel ref if available
            editorParams: {
                data_point_type: { ...validatedDataPointType },
                sub_category: { ...validatedSubCategory },
                data_point_name: { ...validatedDataPointName },
                minimum_score: minimumScoreEditor ? { ...minimumScoreEditor } : null,
                maximum_score: maximumScoreEditor ? { ...maximumScoreEditor } : null,
                score_detail_type: validatedScoreDetailType ? { ...validatedScoreDetailType } : null,
                target_set: validatedTargetSet ? { ...validatedTargetSet } : null,
                target_descriptor: validatedTargetDescriptors ? [...validatedTargetDescriptors] : null,
                school_year: schoolYear ? { ...schoolYear } : null,
            },
            chipText: validatedSubCategory.display_name || validatedSubCategory.name,
            tooltipData: (() => {
                const tooltip = {
                    'Assessment Group': validatedDataPointType.name,
                    'Assessment': validatedSubCategory.display_name || validatedSubCategory.name,
                    'Window': validatedDataPointName.alias,
                }
                const minScore = getScoreTooltipValue(minimumScoreEditor)
                const maxScore = getScoreTooltipValue(maximumScoreEditor)
                if (minScore !== null) tooltip['Minimum Score'] = minScore
                if (maxScore !== null) tooltip['Maximum Score'] = maxScore
                if (validatedScoreDetailType) tooltip['Score Detail Type'] = validatedScoreDetailType.description || validatedScoreDetailType.code
                if (validatedTargetSet) tooltip['Performance Band'] = validatedTargetSet.name
                if (validatedTargetDescriptors?.length) tooltip['Target Descriptor'] = validatedTargetDescriptors.map(d => d.name).join(', ')
                if (schoolYear) tooltip['School Year'] = schoolYear.name
                return tooltip
            })(),
            grouper: {
                id: uniqueId,
                name: `${validatedSubCategory.display_name || validatedSubCategory.name} — ${validatedDataPointName.alias}`,
                type: 'controller',
                args: { property: `filters.${uniqueId}.value` }
            },
        })

        // Check if this exact filter already exists (within this batch)
        const existingMatchingFilter = findDuplicateAssessmentFilter(params.filters, filterParams)

        if (existingMatchingFilter) {
            // Exact filter already exists, don't add duplicate
            msg.content = `**${validatedDataPointType.name}** — **${validatedSubCategory.display_name || validatedSubCategory.name}** — **${validatedDataPointName.alias}** filter is already applied`
            return null
        }

        // Add the new filter to current state
        // Note: MainPanel clears all assessment filters before processing a batch,
        // so this just adds each filter the agent sends
        const updatedFilters = [...params.filters, filter]
        let updatedGroupers = groupers
        if (filter.grouper) {
            updatedGroupers = [...groupers, filter.grouper]
        }

        const message = `**${validatedDataPointType.name}** — **${validatedSubCategory.display_name || validatedSubCategory.name}** — **${validatedDataPointName.alias}** Filters Applied`
        return { filter, updatedFilters, updatedGroupers, message }

    } catch (error) {
        console.error('Error applying assessment filter:', error)
        msg.content = `Error applying filter: ${error.message}`
        return null
    }
}

function toNumberOrNull(value) {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

function toAssessmentScoreEditorValue(score, alphaMaps = []) {
    if (score === null || score === undefined || score === '') return null

    const scoreString = String(score).trim()
    const numericScore = toNumberOrNull(score)
    const maps = Array.isArray(alphaMaps) ? alphaMaps : []

    if (maps.length) {
        if (numericScore !== null) {
            const matchedByNumeric = maps.find(m => {
                const mapValue = toNumberOrNull(m.numeric_score)
                if (mapValue === null) return false
                return mapValue === numericScore || mapValue === Math.floor(numericScore)
            })
            if (matchedByNumeric) return { ...matchedByNumeric }
        }

        const matchedByAlpha = maps.find(m =>
            String(m.alpha_score || '').toLowerCase() === scoreString.toLowerCase()
        )
        if (matchedByAlpha) return { ...matchedByAlpha }
    }

    return numericScore !== null
        ? { value: numericScore, numeric_score: numericScore }
        : { value: scoreString, numeric_score: null }
}

function getScoreParamValue(scoreObj) {
    if (!scoreObj) return null
    return scoreObj.value || scoreObj.numeric_score || null
}

function getScoreTooltipValue(scoreObj) {
    if (!scoreObj) return null
    return scoreObj.value || scoreObj.alpha_score || null
}

/**
 * Find a duplicate assessment filter in the filters array.
 * @param {Array} filters - Array of existing filters
 * @param {Object} newFilterParams - Params of the new filter to check
 * @returns {Object|null} Matching filter or null
 */
export function findDuplicateAssessmentFilter(filters, newFilterParams) {
    const sameDescriptorIds = (a, b) => {
        if (!a && !b) return true
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
        return JSON.stringify([...a].sort()) === JSON.stringify([...b].sort())
    }

    return filters.find(f =>
        f.type === 'assessment' &&
        f.params.data_point_type_id === newFilterParams.data_point_type_id &&
        f.params.sub_category_id === newFilterParams.sub_category_id &&
        f.params.data_point_name_id === newFilterParams.data_point_name_id &&
        f.params.minimum_score === newFilterParams.minimum_score &&
        f.params.maximum_score === newFilterParams.maximum_score &&
        (f.params.score_detail_type_id ?? null) === (newFilterParams.score_detail_type_id ?? null) &&
        (f.params.target_set_id ?? null) === (newFilterParams.target_set_id ?? null) &&
        sameDescriptorIds(f.params.target_descriptor_id, newFilterParams.target_descriptor_id)
    )
}
